import { aggregateUseCase, findObjectUseCase } from "../../usecases/object";
import {
  getScheduleForDay,
  getCycleDayInfo,
  getEffectiveScheduleAssignmentsForMonth,
} from "../../pages/system-administration/manual-timesheets/ScheduleSequences";
import {
  getEmploymentTypeAssignmentsForMonth,
  getTwentyFourSevenAssignmentsForMonth,
  getEffectiveEmploymentTypeForDay,
  getEffectiveTwentyFourSevenForDay,
} from "../../pages/system-administration/manual-timesheets/EmployeeAssignments";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const dateKey = (v) =>
  v == null ? null : new Date(v).toISOString().split("T")[0];

const shiftDateKey = (dk, days) => {
  if (!dk) return null;
  const d = new Date(dk + "T12:00:00.000Z");
  d.setUTCDate(d.getUTCDate() + days);
  return dateKey(d);
};
const nextDateKey = (dk) => shiftDateKey(dk, 1);
const prevDateKey = (dk) => shiftDateKey(dk, -1);

const arr = (doc, key) => (Array.isArray(doc?.[key]) ? doc[key] : []);

const TWENTY_FOUR_SEVEN_HOLIDAY_EXEMPT = ["9 Hours", "12 Hours"];

const firstPresent = (...values) =>
  values.find((value) => value != null && value !== "");

const isHolidayExemptTwentyFourSeven = (value) =>
  TWENTY_FOUR_SEVEN_HOLIDAY_EXEMPT.includes(value);

const getEffectiveTwentyFourSevenValue = (
  log,
  dk,
  assignments = [],
  emp,
) =>
  firstPresent(
    log?.twentyFourSeven,
    getEffectiveTwentyFourSevenForDay(assignments, dk),
    emp?.twentyFourSeven,
  );

function orgMatch(emp, doc) {
  if (doc?.selectAll) return true;
  for (const [docKey, empKey] of [
    ["plantilla", "cluster"],
    ["units", "unit"],
    ["sections", "section"],
    ["divisions", "division"],
    ["offices", "office"],
    ["regions", "region"],
  ]) {
    const list = arr(doc, docKey);
    if (list.length) return emp?.[empKey] != null && list.includes(emp[empKey]);
  }
  return false;
}

const parseTime = (t) => {
  if (!t || t === "--:--") return null;
  const [h = 0, m = 0] = t.split(":").map(Number);
  const v = h * 60 + m;
  return Number.isNaN(v) ? null : v;
};

const isMorningClockTime = (t) => {
  const mins = parseTime(t);
  return mins != null && mins > 0 && mins < 12 * 60;
};

/** AM OUT or morning timeOut on a log row (graveyard shift end). */
const getGraveyardMorningOut = (log) => {
  if (!log) return null;
  if (log.timeAmOut && log.timeAmOut !== "--:--") return log.timeAmOut;
  if (
    log.timeOut &&
    log.timeOut !== "--:--" &&
    isMorningClockTime(log.timeOut)
  ) {
    return log.timeOut;
  }
  return null;
};

/** Same-night PM OUT only (excludes morning timeOut misfiled on the work night). */
const getGraveyardPmOut = (log) => {
  if (!log?.timeOut || log.timeOut === "--:--") return null;
  return isMorningClockTime(log.timeOut) ? null : log.timeOut;
};

const scheduleTime = (day, key) => {
  if (!day) return null;
  const camel =
    key === "timein" ? "timeIn" : key === "timeout" ? "timeOut" : key;
  const v = day[key] ?? day[camel];
  if (v != null && v !== "") return v;
  const lower = key.toLowerCase();
  return key !== lower ? day[lower] : null;
};

/** Returns true when day/sched describes a graveyard shift. */
const isGraveyardDay = (day, sched) =>
  !!day &&
  (day.day?.includes(" - ") ||
    day.day?.includes("Graveyard") ||
    /Graveyard/i.test(sched?.selectedOption ?? "") ||
    /Graveyard/i.test(sched?.name ?? ""));

const isGraveyardSchedule = (sched) =>
  /Graveyard/i.test(sched?.selectedOption ?? "") ||
  /Graveyard/i.test(sched?.name ?? "");

/** Checks whether `dk` falls inside any [start, end] pair in a flat date-range array. */
const inDateRange = (ranges, dk) => {
  const flat = (ranges ?? []).flat(Infinity);
  for (let i = 0; i + 1 < flat.length; i += 2) {
    const [fk, tk] = [dateKey(flat[i]), dateKey(flat[i + 1])];
    if (fk && tk && dk >= fk && dk <= tk) return true;
  }
  return false;
};

/** Checks whether `dk` appears in a manual_dtr selectDate array. */
const inSelectDate = (m, dk) =>
  (Array.isArray(m?.selectDate)
    ? m.selectDate.flatMap((d) => (Array.isArray(d) ? d : [d]))
    : [m?.selectDate]
  ).some((d) => dateKey(d) === dk);

const mapLeaveType = (type) => {
  if (!type) return type;
  const special = {
    "SOLO PARENT LEAVE": "SOLO",
    "STUDY LEAVE": "STUDY",
    "SPECIAL LEAVE BENEFITS FOR WOMEN": "MC",
    "SPECIAL EMERGENCY LEAVE": "CALAMITY",
  };
  return (
    special[type.toUpperCase()] ||
    type
      .split(" ")
      .filter(Boolean)
      .map((w) => w[0].toUpperCase())
      .join("")
  );
};

// ─── Lookup data ──────────────────────────────────────────────────────────────

/** Run async factories with a max in-flight count (preserves result order). */
async function runWithConcurrency(factories, concurrency) {
  const limit =
    !concurrency || concurrency >= factories.length
      ? factories.length
      : Math.max(1, concurrency);
  if (limit >= factories.length) {
    return Promise.all(factories.map((fn) => fn()));
  }
  const results = new Array(factories.length);
  let next = 0;
  const worker = async () => {
    while (next < factories.length) {
      const i = next++;
      results[i] = await factories[i]();
    }
  };
  await Promise.all(
    Array.from({ length: Math.min(limit, factories.length) }, () => worker()),
  );
  return results;
}

/**
 * Org-scoped holidays/suspensions for a set of employees (fetch once, reuse).
 * Used by Reports to avoid re-querying per employee.
 *
 * Do NOT build a large `$or` from every employee org value — the API rejects
 * oversized `$or` payloads. Fetch by date range, then keep rows that match
 * any employee via orgMatch (same filter BiometricDTR uses per day).
 */
export async function prefetchOrgLookups(employees, start, end) {
  const [s, e] = [dateKey(start), dateKey(end)];
  const orgKeys = [
    "type",
    "date",
    "dateFrom",
    "dateTo",
    "selectAll",
    "regions",
    "offices",
    "divisions",
    "sections",
    "units",
    "plantilla",
  ];
  const find = (col, where, keys) =>
    findObjectUseCase().execute(col, { where, keys, limit: 1000 });

  const holidaysRaw =
    (await find("holidays", { date: { $gte: s, $lte: e } }, orgKeys)) || [];
  const suspensionRaw =
    (await find(
      "suspension",
      { dateFrom: { $lte: e }, dateTo: { $gte: s } },
      orgKeys,
    )) || [];

  const empList = (employees || []).filter(Boolean);
  const relevant = (rows) => {
    if (!empList.length) return rows || [];
    return (rows || []).filter((doc) =>
      empList.some((emp) => orgMatch(emp, doc)),
    );
  };

  return {
    holidays: relevant(holidaysRaw),
    suspension: relevant(suspensionRaw),
  };
}

/**
 * Batch employee-scoped attendance lookups once for Reports.
 * One find per collection using date `where` (no `$in` loops); filter by employee id client-side.
 * Only used by ReportService — Timesheet still calls BiometricDTR per employee.
 */
export async function prefetchEmployeeLookups(employees, start, end) {
  const ids = (employees || []).map((emp) => emp?.id).filter(Boolean);
  if (ids.length === 0) {
    return {
      leave_request: [],
      manual_dtr: [],
      ob_management: [],
      slide: [],
      timeoff: [],
    };
  }
  const [s, e] = [dateKey(start), dateKey(end)];
  const idSet = new Set(ids);
  const find = findObjectUseCase();
  const LIMIT = 5000;

  const findOnce = async (collection, where, keys) => {
    const rows =
      (await find.execute(collection, {
        where,
        keys,
        limit: LIMIT,
      })) || [];
    if (ids.length === 1) {
      const only = ids[0];
      return rows.filter((row) => rowEmployeeId(row) === only);
    }
    return rows.filter((row) => idSet.has(rowEmployeeId(row)));
  };

  // Single employee: exact pointer where. Many: date where only, filter client-side.
  const empPointerWhere =
    ids.length === 1 ? { "employee.id": ids[0] } : {};
  const empManualWhere =
    ids.length === 1 ? { "employees.id": ids[0] } : {};

  const leave_request = await findOnce(
    "leave_request",
    {
      stats: "Approved",
      ...empPointerWhere,
      dateRange: {
        $elemMatch: { $elemMatch: { 0: { $lte: e }, 1: { $gte: s } } },
      },
    },
    ["selectedType", "dateRange", "employee"],
  );

  const manual_dtr = await findOnce(
    "manual_dtr",
    {
      ...empManualWhere,
      dateRange: {
        $elemMatch: { $elemMatch: { 0: { $lte: e }, 1: { $gte: s } } },
      },
    },
    ["categoryType", "dateRange", "employees"],
  );

  const ob_management = await findOnce(
    "ob_management",
    {
      status: "Approved",
      ...empPointerWhere,
      selectedDate: {
        $elemMatch: { $elemMatch: { 0: { $lte: e }, 1: { $gte: s } } },
      },
    },
    ["selectedDate", "employee"],
  );

  const slide = await findOnce(
    "slide",
    { ...empPointerWhere, date: { $gte: s, $lte: e } },
    ["date", "employee"],
  );

  const timeoff = await findOnce(
    "timeoff",
    { ...empPointerWhere, date: { $gte: s, $lte: e } },
    ["type", "date", "employee"],
  );

  return { leave_request, manual_dtr, ob_management, slide, timeoff };
}

function rowEmployeeId(row) {
  if (!row) return null;
  return (
    row.employee?.id ||
    row.employees?.id ||
    (typeof row.employee === "string" ? row.employee : null) ||
    row.employee?.objectId ||
    null
  );
}

function filterRowsForEmployee(rows, empId) {
  if (!empId || !Array.isArray(rows)) return rows || [];
  return rows.filter((row) => rowEmployeeId(row) === empId);
}

async function fetchLookupData(emp, start, end, options = {}) {
  const id = emp?.id ?? null;
  const [s, e] = [dateKey(start), dateKey(end)];
  const orgOr = [
    { regions: emp?.region },
    { offices: emp?.office },
    { divisions: emp?.division },
    { sections: emp?.section },
    { units: emp?.unit },
    { plantilla: emp?.cluster },
    { selectAll: true },
  ];
  const find = (col, where, keys) =>
    findObjectUseCase().execute(col, { where, keys });
  const shared = options.sharedLookups || {};
  const lookupConcurrency = options.lookupConcurrency;
  // Reports (serial) must not send large/unsupported $or — date scope + orgMatch.
  const dateScopedOrg = options.serial === true || options.dateScopedOrg === true;
  const holidayKeys = [
    "type",
    "date",
    "selectAll",
    "regions",
    "offices",
    "divisions",
    "sections",
    "units",
    "plantilla",
  ];
  const suspensionKeys = [
    "type",
    "dateFrom",
    "dateTo",
    "selectAll",
    "regions",
    "offices",
    "divisions",
    "sections",
    "units",
    "plantilla",
  ];

  const factories = [
    () =>
      shared.holidays
        ? Promise.resolve(shared.holidays)
        : find(
            "holidays",
            dateScopedOrg
              ? { date: { $gte: s, $lte: e } }
              : { $or: orgOr, date: { $gte: s, $lte: e } },
            holidayKeys,
          ),
    () =>
      shared.suspension
        ? Promise.resolve(shared.suspension)
        : find(
            "suspension",
            dateScopedOrg
              ? { dateFrom: { $lte: e }, dateTo: { $gte: s } }
              : { $or: orgOr, dateFrom: { $lte: e }, dateTo: { $gte: s } },
            suspensionKeys,
          ),
    () =>
      shared.leave_request
        ? Promise.resolve(filterRowsForEmployee(shared.leave_request, id))
        : find(
            "leave_request",
            {
              stats: "Approved",
              "employee.id": id,
              dateRange: {
                $elemMatch: { $elemMatch: { 0: { $lte: e }, 1: { $gte: s } } },
              },
            },
            ["selectedType", "dateRange"],
          ),
    () =>
      shared.manual_dtr
        ? Promise.resolve(filterRowsForEmployee(shared.manual_dtr, id))
        : find(
            "manual_dtr",
            {
              "employees.id": id,
              dateRange: {
                $elemMatch: { $elemMatch: { 0: { $lte: e }, 1: { $gte: s } } },
              },
            },
            ["categoryType", "dateRange"],
          ),
    () =>
      shared.ob_management
        ? Promise.resolve(filterRowsForEmployee(shared.ob_management, id))
        : find(
            "ob_management",
            {
              "employee.id": id,
              status: "Approved",
              selectedDate: {
                $elemMatch: { $elemMatch: { 0: { $lte: e }, 1: { $gte: s } } },
              },
            },
            ["selectedDate"],
          ),
    () =>
      shared.slide
        ? Promise.resolve(filterRowsForEmployee(shared.slide, id))
        : find("slide", { employee: { id }, date: { $gte: s, $lte: e } }, [
            "date",
          ]),
    () =>
      shared.timeoff
        ? Promise.resolve(filterRowsForEmployee(shared.timeoff, id))
        : find("timeoff", { "employee.id": id, date: { $gte: s, $lte: e } }, [
            "type",
            "date",
          ]),
    () => getEmploymentTypeAssignmentsForMonth(id, e),
    () => getTwentyFourSevenAssignmentsForMonth(id, e),
  ];

  const [
    holidays,
    suspension,
    leave_request,
    manual_dtr,
    ob_management,
    slide,
    timeoff,
    employment_type_assignments,
    twenty_fourSeven_assignments,
  ] = await runWithConcurrency(factories, lookupConcurrency);

  return {
    holidays,
    suspension,
    leave_request,
    manual_dtr,
    ob_management,
    slide,
    timeoff,
    employment_type_assignments,
    twenty_fourSeven_assignments,
  };
}

// ─── Apply lookup stats ───────────────────────────────────────────────────────

function applyLookupStats(
  logs,
  emp,
  {
    holidays,
    suspension,
    leave_request,
    manual_dtr,
    ob_management,
    timeoff,
    twenty_fourSeven_assignments,
  } = {},
) {
  return logs.map((log) => {
    const dk = log.date ?? dateKey(log.stampDate);
    const effectiveTwentyFourSeven = getEffectiveTwentyFourSevenValue(
      log,
      dk,
      twenty_fourSeven_assignments ?? [],
      emp,
    );

    let stats = Array.isArray(log.timeRecStats) ? [...log.timeRecStats] : [];
    const excludeHolidayLike = isHolidayExemptTwentyFourSeven(
      effectiveTwentyFourSeven,
    );

    const stamp = log.stampDate ? new Date(log.stampDate) : null;

    stats = stats.concat(
      excludeHolidayLike
        ? []
        : holidays
            .filter((h) => dateKey(h?.date) === dk && orgMatch(emp, h))
            .map((h) => h.type)
            .filter(Boolean),
      (timeoff ?? [])
        .filter((t) => dateKey(t?.date) === dk)
        .map((t) => t.type || "TIMEOFF")
        .filter(Boolean),
      excludeHolidayLike
        ? []
        : suspension
            .filter((s) => {
              const [from, to] = [
                s?.dateFrom && new Date(s.dateFrom),
                s?.dateTo && new Date(s.dateTo),
              ];
              return (
                from &&
                to &&
                stamp &&
                stamp >= from &&
                stamp <= to &&
                orgMatch(emp, s)
              );
            })
            .map((s) => s.type)
            .filter(Boolean),
      leave_request
        .filter((lr) => inDateRange(lr?.dateRange, dk))
        .map((lr) => mapLeaveType(lr?.selectedType))
        .filter(Boolean),
      manual_dtr
        .filter((m) => inDateRange(m?.dateRange, dk))
        .map((m) => m.categoryType)
        .filter(Boolean),
      ob_management
        .filter((ob) => inDateRange(ob?.selectedDate, dk))
        .map(() => "Official Business"),
    );

    return { ...log, timeRecStats: stats };
  });
}

// ─── Lookup flags ─────────────────────────────────────────────────────────────

function getLookupFlags(dk, stamp, emp, lookupData) {
  const {
    holidays,
    suspension,
    timeoff,
    slide,
    manual_dtr,
    leave_request,
    ob_management,
  } = lookupData;
  return {
    hasHoliday: holidays.some(
      (h) => dateKey(h?.date) === dk && orgMatch(emp, h),
    ),
    hasSuspension: suspension.some((s) => {
      const [f, t] = [
        s?.dateFrom && new Date(s.dateFrom),
        s?.dateTo && new Date(s.dateTo),
      ];
      return f && t && stamp >= f && stamp <= t && orgMatch(emp, s);
    }),
    hasTimeoff: timeoff.some((t) => dateKey(t?.date) === dk),
    hasSlide: slide.some((s) => dateKey(s?.date) === dk),
    hasManualDtr: manual_dtr.some((m) => inDateRange(m?.dateRange, dk)),
    hasLeave: leave_request.some((lr) => inDateRange(lr?.dateRange, dk)),
    hasObManagement: ob_management.some((ob) =>
      inDateRange(ob?.selectedDate, dk),
    ),
  };
}

// ─── Late / undertime / absent ────────────────────────────────────────────────

const EMPTY = {
  basicLateMinutes: "",
  basicUndertimeMinutes: "",
  APlateMinutes: "",
  APundertimeMinutes: "",
  printTimeRecStats: [],
};

const BASIC_THRESHOLDS = { lateMax: 180, undertimeMax: 120, totalMax: 240 };
const AP_THRESHOLD = 61;

const todayDateKey = () => {
  const now = new Date();
  now.setDate(now.getDate() - 1);
  return [
    now.getFullYear(),
    String(now.getMonth() + 1).padStart(2, "0"),
    String(now.getDate()).padStart(2, "0"),
  ].join("-");
};

function buildPlaceholders(
  logByDate,
  assignments,
  endIso,
  todayKey,
  emp,
  lookupData,
) {
  const end = new Date(endIso);
  const placeholders = [];
  for (
    let d = new Date(Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), 1));
    d <= end;
    d.setUTCDate(d.getUTCDate() + 1)
  ) {
    const dk = dateKey(d);
    if (dk > todayKey) break;
    if (logByDate.has(dk)) continue;

    const sched = assignments.find(
      (a) => a?.created && dateKey(a.created) <= dk,
    );
    const hasSchedule = !!getScheduleForDay(sched, new Date(d));

    if (!hasSchedule) {
      // No schedule — still create a placeholder if there's lookup data
      if (lookupData && emp) {
        const stamp = new Date(`${dk}T00:00:00.000Z`);
        const {
          hasHoliday,
          hasSuspension,
          hasTimeoff,
          hasManualDtr,
          hasLeave,
          hasObManagement,
        } = getLookupFlags(dk, stamp, emp, lookupData);
        const hasAnyLookup =
          hasHoliday ||
          hasSuspension ||
          hasTimeoff ||
          hasManualDtr ||
          hasLeave ||
          hasObManagement;
        if (!hasAnyLookup) continue;
      } else {
        continue;
      }
    }

    placeholders.push({
      date: dk,
      stampDate: new Date(`${dk}T00:00:00.000Z`),
      timeIn: "--:--",
      timeOut: "--:--",
      timeAmOut: "--:--",
      timePmIn: "--:--",
      timeRecStats: [],
    });
  }
  return placeholders;
}

const applyLateUndertimeStats = async (logs, emp, lookupData = {}, endIso) => {
  const employeeId = emp?.id;
  if (!employeeId) return [];

  const todayKey = todayDateKey();
  const {
    employment_type_assignments = [],
    twenty_fourSeven_assignments = [],
  } = lookupData;

  const effectiveEmploymentTypeName = (dk) =>
    getEffectiveEmploymentTypeForDay(employment_type_assignments, dk) ??
    emp?.employmentType?.name;
  const effectiveTwentyFourSeven = (dk, log) =>
    getEffectiveTwentyFourSevenValue(
      log,
      dk,
      twenty_fourSeven_assignments,
      emp,
    );

  const assignments = await getEffectiveScheduleAssignmentsForMonth(
    employeeId,
    endIso,
  );
  const logByDate = new Map(
    logs.map((l) => [l.date ?? dateKey(l.stampDate), l]),
  );
  const placeholders = buildPlaceholders(
    logByDate,
    assignments,
    endIso,
    todayKey,
    emp,
    lookupData,
  );

  const allLogs = placeholders.length
    ? [...logs, ...placeholders].sort(
        (a, b) =>
          new Date(b.stampDate || b.date) - new Date(a.stampDate || a.date),
      )
    : logs;

  if (placeholders.length) {
    logByDate.clear();
    allLogs.forEach((l) => logByDate.set(l.date ?? dateKey(l.stampDate), l));
  }

  const processed = [];

  for (const log of allLogs) {
    const stamp = log.stampDate ? new Date(log.stampDate) : null;
    if (!stamp) {
      processed.push({ ...log, ...EMPTY });
      continue;
    }

    const dk = dateKey(stamp);
    let sched =
      assignments.find((a) => a?.created && dateKey(a.created) <= dk) ?? null;
    let day = getScheduleForDay(sched, stamp);
    let isTimeoutOnlyDay = false;

    const prevDk = prevDateKey(dk);
    const prevStamp = new Date(prevDk + "T12:00:00.000Z");
    const prevSched =
      assignments.find((a) => a?.created && dateKey(a.created) <= prevDk) ??
      null;
    const prevDay = getScheduleForDay(prevSched, prevStamp);
    const cycle = getCycleDayInfo(sched, stamp);
    const prevCycle = getCycleDayInfo(prevSched, prevStamp);
    const graveyardCycle =
      isGraveyardSchedule(sched) || isGraveyardSchedule(prevSched);

    // Graveyard N-on/M-off: cycle off-day after a work night = timeout-only
    // carry-over morning (OUT from previous night). Never a scheduled work day.
    if (graveyardCycle && cycle && !cycle.isWorkDay && prevCycle?.isWorkDay) {
      day = prevDay ?? day ?? sched?.scheduleArray?.[0] ?? null;
      sched = prevSched ?? sched;
      isTimeoutOnlyDay = true;
    } else if (!day) {
      // Non-cycle graveyard: show timeout-only rows for previous shift's out
      if (isGraveyardDay(prevDay, prevSched)) {
        day = prevDay;
        sched = prevSched;
        isTimeoutOnlyDay = true;
      }
    } else if (graveyardCycle && cycle && !cycle.isWorkDay) {
      // True off day (not the morning after last work night)
      day = null;
    }

    // scheduleUpdate override
    if (log.scheduleUpdate?.scheduleArray?.length > 0) {
      const dayName = new Date(dk + "T12:00:00.000Z").toLocaleDateString(
        "en-US",
        {
          weekday: "long",
          timeZone: "UTC",
        },
      );
      const overrideDay = log.scheduleUpdate.scheduleArray.find(
        (e) => e.day === dayName,
      );
      if (overrideDay) {
        day = overrideDay;
        sched = log.scheduleUpdate;
        isTimeoutOnlyDay = false;
        // Cycle graveyard: weekday override must not turn final-morning
        // carry-over (cycle day N+1) into a scheduled work day.
        const updCycle = getCycleDayInfo(log.scheduleUpdate, stamp);
        const updPrevCycle = getCycleDayInfo(log.scheduleUpdate, prevStamp);
        if (
          /Graveyard/i.test(log.scheduleUpdate.selectedOption ?? "") &&
          updCycle &&
          !updCycle.isWorkDay &&
          updPrevCycle?.isWorkDay
        ) {
          isTimeoutOnlyDay = true;
        } else if (
          /Graveyard/i.test(log.scheduleUpdate.selectedOption ?? "") &&
          updCycle &&
          !updCycle.isWorkDay
        ) {
          continue;
        }
      } else if (/Graveyard/i.test(log.scheduleUpdate.selectedOption ?? "")) {
        // Graveyard timeOut falls on the next calendar day — check if the
        // previous day had a graveyard entry in the scheduleUpdate array
        const prevDayName = new Date(
          prevDk + "T12:00:00.000Z",
        ).toLocaleDateString("en-US", {
          weekday: "long",
          timeZone: "UTC",
        });
        const prevOverrideDay = log.scheduleUpdate.scheduleArray.find(
          (e) => e.day === prevDayName,
        );
        if (prevOverrideDay) {
          day = prevOverrideDay;
          sched = log.scheduleUpdate;
          isTimeoutOnlyDay = true;
        } else {
          continue;
        }
      } else {
        continue;
      }
    }

    // if (!day) continue;
    if (!day) {
      const isNineOrTwelve = isHolidayExemptTwentyFourSeven(
        effectiveTwentyFourSeven(dk, log),
      );
      const flags = getLookupFlags(dk, stamp, emp, lookupData);
      const {
        hasHoliday,
        hasSuspension,
        hasTimeoff,
        hasManualDtr,
        hasLeave,
        hasObManagement,
      } = flags;

      const hasAnyLookup =
        (!isNineOrTwelve && (hasHoliday || hasSuspension)) ||
        hasTimeoff ||
        hasManualDtr ||
        hasLeave ||
        hasObManagement;

      if (hasAnyLookup) {
        const stats = [...(log.timeRecStats ?? [])];
        const uniqPush = (xs) =>
          xs.forEach((x) => x && !stats.includes(x) && stats.push(x));

        [
          [
            !isNineOrTwelve && hasHoliday,
            lookupData.holidays,
            (h) => dateKey(h?.date) === dk && orgMatch(emp, h),
            (h) => h.type,
          ],
          [
            hasTimeoff,
            lookupData.timeoff,
            (t) => dateKey(t?.date) === dk,
            (t) => t.type || "TIMEOFF",
          ],
          [
            !isNineOrTwelve && hasSuspension,
            lookupData.suspension,
            (s) => {
              const [f, t] = [
                s?.dateFrom && new Date(s.dateFrom),
                s?.dateTo && new Date(s.dateTo),
              ];
              return f && t && stamp >= f && stamp <= t && orgMatch(emp, s);
            },
            (s) => s.type,
          ],
          [
            hasLeave,
            lookupData.leave_request,
            (lr) => inDateRange(lr?.dateRange, dk),
            (lr) => mapLeaveType(lr?.selectedType),
          ],
          [
            hasManualDtr,
            lookupData.manual_dtr,
            (m) => inDateRange(m?.dateRange, dk),
            (m) => m.categoryType,
          ],
          [
            hasObManagement,
            lookupData.ob_management,
            (ob) => inDateRange(ob?.selectedDate, dk),
            () => "Official Business",
          ],
        ].forEach(
          ([on, list, ok, pick]) =>
            on && uniqPush((list ?? []).filter(ok).map(pick)),
        );

        processed.push({
          ...log,
          timeRecStats: stats,
          basicLateMinutes: "",
          basicUndertimeMinutes: "",
          APlateMinutes: "",
          APundertimeMinutes: "",
          printTimeRecStats: stats,
        });
      }

      continue;
    }

    // Future dates: no absent/late marking
    if (dk > todayKey) {
      processed.push({
        ...log,
        timeRecStats: [...(log.timeRecStats ?? [])],
        basicLateMinutes: "",
        basicUndertimeMinutes: "",
        APlateMinutes: "",
        APundertimeMinutes: "",
        printTimeRecStats: [...(log.timeRecStats ?? [])],
      });
      continue;
    }

    const isJO = ["Job Order", "JO"].includes(effectiveEmploymentTypeName(dk));
    const isGraveyard =
      isGraveyardDay(day, sched) || isGraveyardSchedule(sched);
    const isNineOrTwelve = isHolidayExemptTwentyFourSeven(
      effectiveTwentyFourSeven(dk, log),
    );

    const flags = getLookupFlags(dk, stamp, emp, lookupData);
    const {
      hasHoliday,
      hasSuspension,
      hasTimeoff,
      hasSlide,
      hasManualDtr,
      hasLeave,
      hasObManagement,
    } = flags;

    const skip =
      hasTimeoff ||
      hasManualDtr ||
      hasLeave ||
      hasObManagement ||
      (!isNineOrTwelve && (hasHoliday || hasSuspension));

    // ── Punch times ────────────────────────────────────────────────────────
    // Graveyard work night: IN = PM IN, or same-night PM OUT when PM IN is
    // missing (misfiled punch). OUT = next-day morning first, then PM OUT.
    // Today's morning AM OUT belongs to the previous night.
    const hasPmIn = !!(log.timePmIn && log.timePmIn !== "--:--");
    const rawAmOut = getGraveyardMorningOut(log);
    const rawPmOut = getGraveyardPmOut(log);
    const hasShiftIn = hasPmIn || !!rawPmOut;
    const tin = isGraveyard
      ? hasPmIn
        ? log.timePmIn
        : rawPmOut || "--:--"
      : log.timeIn !== "--:--"
        ? log.timeIn
        : log.timePmIn;
    const tout = isGraveyard
      ? rawAmOut || rawPmOut || "--:--"
      : log.timeOut !== "--:--"
        ? log.timeOut
        : log.timeAmOut;

    let effectiveTout = tout;
    let outBelongsToPrevShift = false;
    const nextLog = isGraveyard ? logByDate.get(nextDateKey(dk)) : null;
    const nextMorningOut = getGraveyardMorningOut(nextLog);

    if (isGraveyard && !isTimeoutOnlyDay) {
      const morningAmOut =
        rawAmOut && isMorningClockTime(rawAmOut) ? rawAmOut : null;

      // Morning OUT with no shift IN = previous night's OUT on this calendar day
      outBelongsToPrevShift = !hasShiftIn && !!morningAmOut;
      // Next-day morning OUT first; same-night PM OUT only when no next-morning punch
      effectiveTout = hasShiftIn
        ? nextMorningOut || rawPmOut || "--:--"
        : "--:--";
    }

    const tinM = parseTime(tin);
    const toutM = parseTime(effectiveTout);
    const noIn = !tin || tin === "--:--";
    const noOut = !effectiveTout || effectiveTout === "--:--";
    const noOutForAbsent = noOut;

    // ── Schedule times ─────────────────────────────────────────────────────
    const sIn = parseTime(
      scheduleTime(day, "lateTimeIn") || scheduleTime(day, "timein"),
    );
    const sOut = parseTime(
      scheduleTime(day, "earliestTimeOut") || scheduleTime(day, "timeout"),
    );
    const apIn = parseTime(scheduleTime(day, "timein"));
    const apOut = parseTime(scheduleTime(day, "timeout"));

    const isGraveyardCarryOver =
      isGraveyard && (isTimeoutOnlyDay || outBelongsToPrevShift);
    const graveyardSameDayTimeout =
      isGraveyard &&
      !isTimeoutOnlyDay &&
      rawPmOut &&
      effectiveTout === rawPmOut;
    const nextDayMissingAmOut = isGraveyard && !nextMorningOut;

    const adjustOut = (base, crosses) =>
      isGraveyard &&
      crosses &&
      !isGraveyardCarryOver &&
      graveyardSameDayTimeout &&
      nextDayMissingAmOut &&
      base != null &&
      toutM != null &&
      toutM > base
        ? base + 24 * 60
        : base;

    const sOutForUT = adjustOut(
      sOut,
      sIn != null && sOut != null && sOut < sIn,
    );
    const apOutForUT = adjustOut(
      apOut,
      apIn != null && apOut != null && apOut < apIn,
    );

    const basicLate =
      sIn != null && tinM != null ? Math.max(0, tinM - sIn) : null;
    const basicUT =
      sOutForUT != null && toutM != null
        ? Math.max(0, sOutForUT - toutM)
        : null;
    const apLate =
      apIn != null && tinM != null ? Math.max(0, tinM - apIn) : null;
    const apUT =
      apOutForUT != null && toutM != null
        ? Math.max(0, apOutForUT - toutM)
        : null;

    const bL = basicLate ?? 0,
      bUT = basicUT ?? 0,
      aL = apLate ?? 0,
      aUT = apUT ?? 0;

    // ── Absent logic ───────────────────────────────────────────────────────
    // Scheduled work night → absent when this shift's IN/OUT are missing.
    // Timeout-only mornings (day after last work night) → never absent / NO AP.
    // Carry-over morning OUT on a work night does not count as this night's OUT.
    let absent = isJO ? noIn || noOutForAbsent : noIn && noOutForAbsent;
    if (isTimeoutOnlyDay) absent = false;
    if (skip && (noIn || noOutForAbsent)) absent = false;
    if (
      isGraveyard &&
      !isTimeoutOnlyDay &&
      hasShiftIn &&
      nextMorningOut &&
      (noOutForAbsent || absent)
    ) {
      absent = false;
    }

    const { lateMax, undertimeMax, totalMax } = BASIC_THRESHOLDS;
    if (
      (!skip &&
        !absent &&
        !isJO &&
        !isGraveyardCarryOver &&
        ((bL > lateMax && noOut) ||
          (bUT > undertimeMax && noIn) ||
          bL + bUT > totalMax)) ||
      (noIn && noOut && !skip && !isTimeoutOnlyDay)
    ) {
      absent = true;
    }

    // ── printAbsent logic ──────────────────────────────────────────────────
    let printAbsent = absent;
    if (
      !skip &&
      !printAbsent &&
      !isGraveyardCarryOver &&
      (aL > AP_THRESHOLD || aUT > AP_THRESHOLD || aL + aUT > AP_THRESHOLD)
    )
      printAbsent = true;
    if (isJO && (noIn || noOutForAbsent) && !skip && !isTimeoutOnlyDay)
      printAbsent = true;
    if (!isJO && !skip && !isTimeoutOnlyDay && !printAbsent) {
      const graveyardNextHasMorningOut = isGraveyard && !!nextMorningOut;
      if (noIn || (noOutForAbsent && !graveyardNextHasMorningOut))
        printAbsent = true;
    }

    // Safety: timeout-only carry-over mornings never get ABSENT / NO AP
    if (isTimeoutOnlyDay) {
      absent = false;
      printAbsent = false;
    }

    // ── Build timeRecStats ─────────────────────────────────────────────────
    const stats = [...(log.timeRecStats ?? [])];
    if (skip) {
      const uniqPush = (xs) =>
        xs.forEach((x) => x && !stats.includes(x) && stats.push(x));
      [
        [
          !isNineOrTwelve && hasHoliday,
          lookupData.holidays,
          (h) => dateKey(h?.date) === dk && orgMatch(emp, h),
          (h) => h.type,
        ],
        [
          hasTimeoff,
          lookupData.timeoff,
          (t) => dateKey(t?.date) === dk,
          (t) => t.type || "TIMEOFF",
        ],
        [
          !isNineOrTwelve && hasSuspension,
          lookupData.suspension,
          (s) => {
            const [f, t] = [
              s?.dateFrom && new Date(s.dateFrom),
              s?.dateTo && new Date(s.dateTo),
            ];
            return f && t && stamp >= f && stamp <= t && orgMatch(emp, s);
          },
          (s) => s.type,
        ],
        [
          hasLeave,
          lookupData.leave_request,
          (lr) => inDateRange(lr?.dateRange, dk),
          (lr) => mapLeaveType(lr?.selectedType),
        ],
        [
          hasManualDtr,
          lookupData.manual_dtr,
          (m) => inDateRange(m?.dateRange, dk),
          (m) => m.categoryType,
        ],
        [
          hasObManagement,
          lookupData.ob_management,
          (ob) => inDateRange(ob?.selectedDate, dk),
          () => "Official Business",
        ],
      ].forEach(
        ([on, list, ok, pick]) =>
          on && uniqPush((list ?? []).filter(ok).map(pick)),
      );
    }

    if (!skip && !absent && !(isGraveyard && outBelongsToPrevShift)) {
      if (
        tinM != null &&
        sIn != null &&
        tinM > sIn &&
        !stats.some((s) => s.startsWith("LATE"))
      )
        stats.push("LATE");
      if (
        toutM != null &&
        sOut != null &&
        toutM < sOut &&
        !stats.some((s) => s.startsWith("UNDERTIME"))
      )
        stats.push("UNDERTIME");
    }
    if (absent && !stats.includes("ABSENT")) stats.push("ABSENT");

    // ── Build printTimeRecStats ────────────────────────────────────────────
    let print = [...stats];

    if (hasSlide || skip || printAbsent)
      print = print.filter(
        (s) => !s.startsWith("LATE") && !s.startsWith("UNDERTIME"),
      );
    if (hasSlide) print.push("SLIDE");

    // Full miss (no shift IN/OUT) → ABSENT. NO AP only for incomplete punches
    // that still have some shift presence / AP threshold breach.
    const absentLabel = !printAbsent
      ? null
      : absent || (noIn && noOut)
        ? "ABSENT"
        : aL > AP_THRESHOLD || aUT > AP_THRESHOLD || printAbsent
          ? "NO AP"
          : "ABSENT";
    if (absentLabel) {
      print = print.filter((s) => s !== "ABSENT" && s !== "NO AP");
      if (!hasSlide) print.push(absentLabel);
    }

    if (!print.length && !hasSlide && !skip && !printAbsent) {
      if (basicLate || apLate) print.push("LATE");
      if (basicUT || apUT) print.push("UNDERTIME");
    }

    const fmt = (v) =>
      skip ||
      absent ||
      (isGraveyard && outBelongsToPrevShift) ||
      v == null ||
      Number.isNaN(v)
        ? ""
        : v;

    processed.push({
      ...log,
      timeRecStats: stats,
      basicLateMinutes: fmt(basicLate),
      basicUndertimeMinutes: fmt(basicUT),
      APlateMinutes: fmt(apLate),
      APundertimeMinutes: fmt(apUT),
      printTimeRecStats: print,
    });
  }
  return processed;
};

// ─── Biometric logs pipeline ──────────────────────────────────────────────────

const buildBiometricLogsPipeline = (start, end, userID) => {
  const [startDate, endDate] = [start.split("T")[0], end.split("T")[0]];
  const endPlus6h = new Date(
    new Date(end).getTime() + 6 * 60 * 60 * 1000,
  ).toISOString();
  return [
    { $match: { timestamp: { $ne: null }, pin: userID } },
    {
      $match: {
        $expr: {
          $and: [
            { $gte: ["$timestamp", { $toDate: start }] },
            { $lte: ["$timestamp", { $toDate: endPlus6h }] },
          ],
        },
      },
    },
    { $sort: { timestamp: 1 } },
    {
      $addFields: {
        date: {
          $cond: [
            {
              $and: [
                { $in: ["$status", [1, "1"]] },
                {
                  $lt: [
                    {
                      $add: [
                        { $multiply: [{ $hour: "$timestamp" }, 60] },
                        { $minute: "$timestamp" },
                      ],
                    },
                    360,
                  ],
                },
              ],
            },
            {
              $dateToString: {
                date: { $subtract: ["$timestamp", 86400000] },
                format: "%Y-%m-%d",
              },
            },
            { $dateToString: { date: "$timestamp", format: "%Y-%m-%d" } },
          ],
        },
      },
    },
    { $match: { date: { $gte: startDate, $lte: endDate } } },
    {
      $group: {
        _id: { userID: "$pin", date: "$date", status: "$status" },
        minStamp: { $min: "$timestamp" },
        maxStamp: { $max: "$timestamp" },
        doc: { $first: "$$ROOT" },
      },
    },
    {
      $replaceRoot: {
        newRoot: {
          $mergeObjects: [
            "$doc",
            {
              stampDate: {
                $cond: [
                  { $in: ["$_id.status", [0, "0", 3, "3"]] },
                  "$minStamp",
                  "$maxStamp",
                ],
              },
            },
          ],
        },
      },
    },
    {
      $addFields: {
        timeIn: {
          $cond: [
            { $in: ["$status", [0, "0"]] },
            { $dateToString: { date: "$stampDate", format: "%H:%M:%S" } },
            "--:--",
          ],
        },
        timeOut: {
          $cond: [
            { $in: ["$status", [1, "1"]] },
            { $dateToString: { date: "$stampDate", format: "%H:%M:%S" } },
            "--:--",
          ],
        },
        timeAmOut: {
          $cond: [
            { $in: ["$status", [2, "2"]] },
            { $dateToString: { date: "$stampDate", format: "%H:%M:%S" } },
            "--:--",
          ],
        },
        timePmIn: {
          $cond: [
            { $in: ["$status", [3, "3"]] },
            { $dateToString: { date: "$stampDate", format: "%H:%M:%S" } },
            "--:--",
          ],
        },
      },
    },
    { $addFields: { timeRecStats: [] } },
    {
      $group: {
        _id: { userID: "$pin", date: "$date" },
        docs: { $push: "$$ROOT" },
      },
    },
    {
      $replaceRoot: {
        newRoot: {
          id: {
            $let: {
              vars: { d: { $arrayElemAt: ["$docs", 0] } },
              in: { $ifNull: ["$$d.id", "$$d._id"] },
            },
          },
          originalData: {
            $map: {
              input: "$docs",
              as: "d",
              in: {
                stampDate: "$$d.stampDate",
                status: "$$d.status",
                id: { $ifNull: ["$$d.id", "$$d._id"] },
              },
            },
          },
          userID: "$_id.userID",
          date: "$_id.date",
          stampDate: { $toDate: { $concat: ["$_id.date", "T00:00:00.000Z"] } },
          ...["timeIn", "timeOut", "timeAmOut", "timePmIn"].reduce(
            (acc, field, i) => {
              acc[field] = {
                $let: {
                  vars: {
                    d: {
                      $arrayElemAt: [
                        {
                          $filter: {
                            input: "$docs",
                            as: "x",
                            cond: {
                              $in: [
                                `$$x.status`,
                                [[0, 1, 2, 3][i], String([0, 1, 2, 3][i])],
                              ],
                            },
                          },
                        },
                        0,
                      ],
                    },
                  },
                  in: { $ifNull: [`$$d.${field}`, "--:--"] },
                },
              };
              return acc;
            },
            {},
          ),
          timeRecStats: {
            $reduce: {
              input: "$docs",
              initialValue: [],
              in: {
                $setUnion: [
                  "$$value",
                  { $ifNull: ["$$this.timeRecStats", []] },
                ],
              },
            },
          },
          scheduleUpdate: {
            $let: {
              vars: { d: { $arrayElemAt: ["$docs", 0] } },
              in: { $ifNull: ["$$d.scheduleUpdate", null] },
            },
          },
          twentyFourSeven: {
            $let: {
              vars: { d: { $arrayElemAt: ["$docs", 0] } },
              in: { $ifNull: ["$$d.twentyFourSeven", null] },
            },
          },
        },
      },
    },
    { $sort: { date: -1 } },
  ];
};

// ─── Main entry ───────────────────────────────────────────────────────────────

/**
 * @param {number} monthIndex
 * @param {number} year
 * @param {object} employee
 * @param {object} [options]
 * @param {object} [options.sharedLookups]
 * @param {number} [options.lookupConcurrency] max parallel lookup collection calls
 * @param {boolean} [options.serial] run aggregate then lookups sequentially
 */
async function BiometricDTR(monthIndex, year, employee, options = {}) {
  const userID = String(employee?.biometricId ?? employee?.id ?? "");
  const start = new Date(Date.UTC(year, monthIndex, 1)).toISOString();
  const end = new Date(
    Date.UTC(year, monthIndex + 1, 0, 23, 59, 59, 999),
  ).toISOString();

  let data;
  let lookupData;
  if (options.serial) {
    data = await aggregateUseCase().execute(
      "biometric_logs",
      buildBiometricLogsPipeline(start, end, userID),
    );
    lookupData = await fetchLookupData(employee, start, end, options);
  } else {
    [data, lookupData] = await Promise.all([
      aggregateUseCase().execute(
        "biometric_logs",
        buildBiometricLogsPipeline(start, end, userID),
      ),
      fetchLookupData(employee, start, end, options),
    ]);
  }

  const logs = applyLookupStats(
    Array.isArray(data) ? data : [],
    employee,
    lookupData,
  );
  return applyLateUndertimeStats(logs, employee, lookupData, end);
}

export default BiometricDTR;
